import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { DataTransferTypes } from '../../media/mimeConstants';
import { Slice, DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model';
import { EditorView } from '@tiptap/pm/view';

import { cleanHtmlTrailingEmptyParagraphs } from '../../utils/htmlUtils';

type TransformationFn = (text: string) => string;

interface PasteTransformerOptions {
    textTransformationFn?: TransformationFn | null;
    htmlTransformationFn?: TransformationFn | null;
}

const defaultTrimTextFn = (text: string) => text?.trim() || '';
const defaultTrimHtmlFn = (html: string) => cleanHtmlTrailingEmptyParagraphs(html);

/**
 * Cleans the pasted HTML by removing trailing whitespace, then creates a ProseMirror node & replace transaction.
 */
const getHtmlPasteTransaction = (
    html: string,
    view: EditorView,
    htmlTransformationFn: TransformationFn,
): Transaction => {
    const cleanedHtml = htmlTransformationFn(html);
    const parser = ProseMirrorDOMParser.fromSchema(view.state.schema);

    const container = document.createElement('div');
    container.innerHTML = cleanedHtml;

    const parseNode = parser.parse(container);
    const pastedSlice = new Slice(parseNode.content, 1, 1);

    return view.state.tr.replaceSelection(pastedSlice);
};

/**
 * Cleans pasted text by trimming whitespace, then creates a ProseMirror text node & replace transaction.
 */
const getTextPasteTransaction = (
    text: string,
    view: EditorView,
    textTransformationFn: TransformationFn,
): Transaction => {
    const cleanText = textTransformationFn(text);
    const parsedText = view.state.schema.text(cleanText);
    return view.state.tr.replaceSelectionWith(parsedText);
};

/**
 * An extension that allows pasted text / html to be transformed prior to insertion.
 * By default it trims trailing whitespace from pasted text or HTML content.
 */
export const PasteTransformer = Extension.create<PasteTransformerOptions>({
    name: 'textPaste',

    addOptions() {
        return {
            textTransformationFn: defaultTrimTextFn,
            htmlTransformationFn: defaultTrimHtmlFn,
            ...this.parent?.(),
        };
    },

    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: new PluginKey('pasteTransformer'),
                props: {
                    handlePaste: (view, event) => {
                        if (!event.clipboardData) return;

                        const html = event.clipboardData.getData(DataTransferTypes.HTML);
                        const text = event.clipboardData.getData(DataTransferTypes.PLAIN_TEXT);

                        if (!text && !html) return false;

                        event.preventDefault();

                        let transaction: Transaction | null = null;

                        if (html && this.options.htmlTransformationFn) {
                            transaction = getHtmlPasteTransaction(html, view, this.options.htmlTransformationFn);
                        }

                        if (!transaction && text && this.options.textTransformationFn) {
                            transaction = getTextPasteTransaction(text, view, this.options.textTransformationFn);
                        }

                        if (!transaction) return false;

                        view.dispatch(transaction);

                        return true;
                    },
                },
            }),
        ];
    },
});
